import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
    Button,
    CircularProgress,
    Divider,
    Grid,
    IconButton,
    Tooltip,
    withTheme
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import groupBy from 'lodash/groupBy';
import { useSwaggerClient } from '../hooks/useSwaggerClient';
import { formatMoney } from '../utils';
import { AgentCommissionIcon } from '../icons';
import DiscountEditor from '../pages/review/components/DiscountEditor';
import { useModals, MODAL_TYPE } from '../hooks/useModal';
import moment from '../moment-hawaii';
import { useGA } from '../hooks/useGoogleAnalytics';

function hasNegativeAgencyCommission(priceItems) {
    let totals = {};
    for (const item of priceItems) {
        const to = item.account_to;

        if (to === 'client') {
            totals[item?.company_id] =
                (totals[item?.company_id] || 0) + (Number(item.net_usd) || 0);
        }
    }

    return Object.values(totals).some(total => total < 0);
}

const CartItemSummary = ({ flatTickets, showDebug }) => {
    const [showDetails, setShowDetails] = useState(false);
    // we need to find the first ticket, so we can use it's name
    let ticket = flatTickets.find(e => e.type === 'ticket');
    if (!ticket) {
        ticket = flatTickets.find(e => e.type === 'addons');
    }
    if (!ticket) {
        // well, we did something wrong, crash and burn
        throw new Error('Ticket cannot be found in flatTickets', JSON.stringify(flatTickets));
    }

    const total = flatTickets.reduce((sum, { subtotal }) => sum + subtotal, 0);
    return (
        <>
            <Grid container spacing={2}>
                <Grid item className="mr-auto">
                    <h2>{ticket?.activity?.name}</h2>
                </Grid>
                <Grid item>
                    <h2>{formatMoney(total)}</h2>
                </Grid>
            </Grid>
            <Grid container style={{ fontWeight: 300 }}>
                <Grid item>
                    {flatTickets.map(t => {
                        return (
                            <p key={t.id}>
                                {t?.type !== 'discount' ? `${t.quantity} ` : ''}
                                {t?.name} @{' '}
                                {t.subtotal === null ? '?' : formatMoney(t.subtotal / t.quantity)} ={' '}
                                {t.subtotal === null ? '?' : formatMoney(t.subtotal)}
                            </p>
                        );
                    })}
                </Grid>
            </Grid>
            {showDebug && (
                <Grid item>
                    <Button size="small" onClick={() => setShowDetails(x => !x)}>
                        Show calculation
                    </Button>
                    {showDetails && (
                        <pre style={{ fontSize: '80%' }}>
                            {flatTickets.map(t => t.debug.join('\n')).join('\n------\n')}
                        </pre>
                    )}
                </Grid>
            )}
        </>
    );
};

function transformCartItem(item) {
    const transformed = {
        ...item
    };
    delete transformed.availabilityPk;
    delete transformed.providerData;
    return transformed;
}

const BookingSummary = ({
    page,
    cartItems,
    payment_method,
    discount,
    booker,
    customer,
    user,
    isAgent,
    updateCartDiscount,
    children,
    theme,
    showCartLevelDiscounts = false,
    onlyCartItem = false,
    setCartError,
    setEstimatedTotal = false
}) => {
    const [discountAmountError, setDiscountAmountError] = useState(false);
    const [showDetails, setShowDetails] = useState(false);
    const { showModal } = useModals();
    const hasValidCartItems =
        cartItems.length > 0 && cartItems.every(item => item.activity_id && item.start_time);
    const [calculateResponse, loading, error] = useSwaggerClient(
        hasValidCartItems ? 'bookings' : false,
        'calculateBooking',
        {
            newBooking: {
                cartItems:
                    onlyCartItem === false
                        ? cartItems.map(transformCartItem)
                        : [transformCartItem(cartItems[onlyCartItem])],
                booker,
                payment_method,
                discount
            },
            strongVerify: page === 'payment'
        }
    );
    const customerArrival = customer?.arrival_date
        ? moment.utc(customer?.arrival_date).format('ddd, MMM DD YYYY')
        : '–';
    const customerDeparture = customer?.departure_date
        ? moment.utc(customer?.departure_date).format('ddd, MMM DD YYYY')
        : '–';

    useEffect(() => {
        if (!setCartError) {
            return;
        }
        setCartError(error);
    }, [setCartError, error]);

    const flatTickets = calculateResponse?.tickets || [];

    const { track } = useGA();
    const checkoutTracked = useRef(false);

    // Track begin_checkout event when payment page is loaded
    useEffect(() => {
        if (!calculateResponse || checkoutTracked.current === true) {
            return;
        }

        const items = [];
        calculateResponse?.tickets.forEach(ticket => {
            items.push({
                item_id: `${ticket.activity?.id}-${ticket.id}`,
                item_name: `${ticket.activity?.name} | ${ticket.name}`,
                price: ticket.ticket_price || 0,
                quantity: ticket.quantity || 1
            });
        });
        if (page === 'payment') {
            track.beginCheckout({ items, value: calculateResponse?.total || 0 });
        } else if (page === 'cart') {
            track.viewCart({ items, value: calculateResponse?.total || 0, currency: 'USD' });
        }
        checkoutTracked.current = true;
    }, [cartItems, track, page, calculateResponse]);

    const isAdmin = user?.role === 'admin';
    const [showCommission, setShowCommission] = useState(false);
    let sumAgentCommission = 0;
    if (flatTickets !== null) {
        sumAgentCommission = flatTickets
            .map(t =>
                t.price_items
                    .filter(e => e.account_to === 'agent')
                    .map(p => p.net_usd)
                    .reduce((sum, a) => sum + a, 0)
            )
            .reduce((sum, a) => sum + a, 0);
    }
    const showDebug = user && ['admin'].includes(user.role);

    const subTotal =
        flatTickets === null ? 0 : flatTickets.reduce((sum, { subtotal }) => sum + subtotal, 0);
    const taxesAndFees =
        flatTickets === null ? 0 : flatTickets.reduce((sum, { fee }) => sum + fee, 0);
    const groupedFlatTickets =
        flatTickets === null
            ? []
            : Object.values(groupBy(flatTickets.filter(t => t.cartItemGroup !== undefined), e => e.cartItemGroup));

    // profitDebug calculation
    let profitDebug = '';
    const allPriceItems = flatTickets?.reduce((sum, ft) => sum.concat(ft.price_items), []);
    if (showDebug && showDetails && flatTickets !== null) {
        profitDebug = '';
        let idGroupedPriceItems = Object.values(groupBy(allPriceItems, e => e.id));
        idGroupedPriceItems = idGroupedPriceItems.map(e => {
            return {
                id: e[0].id,
                // let's assume the name is fix in each group
                name: e[0].name,
                account_to: e[0].account_to,
                usd: e.reduce((sum, x) => sum + x.net_usd * 100, 0),
                tax: e.reduce((sum, x) => sum + x.tax * 100, 0)
            };
        });
        const profitSum = idGroupedPriceItems.reduce((sum, e) => sum + e.usd, 0);
        const taxSum = idGroupedPriceItems.reduce((sum, e) => sum + e.tax, 0);
        profitDebug = `${idGroupedPriceItems
            .map(t => {
                return `name: "${t.name}" account_to: "${t.account_to}" net profit: ${formatMoney(
                    t.usd / 100
                )} tax: ${formatMoney(t.tax / 100)}`;
            })
            .join('\n')}\n\nSum profit: ${formatMoney(profitSum / 100)} Sum tax: ${formatMoney(
            taxSum / 100
        )} Sum of all: ${formatMoney(profitSum / 100 + taxSum / 100)}`;
    }

    useEffect(() => {
        if (setEstimatedTotal) {
            setEstimatedTotal(calculateResponse?.total);
        }
    }, [calculateResponse, setEstimatedTotal]);

    // create an array of object of the future booking items
    const itemsToBook = flatTickets.map(item => {
        const dateTime = moment(item.start_time);

        return {
            activity: item.activity.name,
            description: item.activity.description,
            subtotal: item.subtotal,
            quantity: item.quantity,
            date: dateTime.format('ddd, MMM DD YYYY'),
            time: dateTime.format('HH:mm a'),
            vendor: item.vendor.name
        };
    });

    // calculate overall price, number of activities and create a summary object
    const bookingsData = {
        itemsToBook,
        bookingItemsCount: itemsToBook.length,
        grandTotal: itemsToBook.reduce((s, ticket) => s + ticket.subtotal, 0),
        discount: discount.agencyDiscount + discount.vendorDiscount,
        taxesAndFees
    };

    const totalCharge = (subTotal * 100 + taxesAndFees * 100) / 100;

    const shouldShowAgentCommissionVarning =
        (isAgent || isAdmin) && hasNegativeAgencyCommission(allPriceItems);

    return (
        <Grid
            item
            style={{
                padding: '30px',
                backgroundColor: '#fafafa',
                opacity: loading ? 0.5 : 1,
                position: 'relative',
                transition: 'opacity 0.3s linear 0.3s'
            }}
        >
            {loading && (
                <div
                    style={{
                        position: 'absolute',
                        left: '50%',
                        top: '50%',
                        transform: 'translateX(-50%) translateY(-50%)'
                    }}
                >
                    <CircularProgress />
                </div>
            )}
            <Grid container direction="column" alignItems="center" className="mb-2">
                <h2>{onlyCartItem !== false ? 'Activity' : 'Booking'} Summary</h2>
                <Grid container alignItems="center" justifyContent="center">
                    {error ? (
                        <span></span>
                    ) : flatTickets !== null ? (
                        <>
                            {onlyCartItem === false && (
                                <span>
                                    {groupedFlatTickets.length} Activit
                                    {groupedFlatTickets.length > 1 ? 'ies' : 'y'}
                                </span>
                            )}
                        </>
                    ) : (
                        <span>
                            {page === 'payment'
                                ? 'Verify tickets with vendors'
                                : 'Updating cart...'}
                        </span>
                    )}
                    {(isAgent || isAdmin) && (
                        <span style={{ position: 'absolute', right: '30px' }}>
                            <Tooltip title="Commission">
                                <IconButton
                                    size="small"
                                    style={{ opacity: showCommission ? 0.6 : 0.15 }}
                                    onClick={() => setShowCommission(!showCommission)}
                                >
                                    <AgentCommissionIcon />
                                </IconButton>
                            </Tooltip>
                        </span>
                    )}
                </Grid>
            </Grid>
            {error && (
                <Alert severity="error">
                    {error.response?.body?.message || 'Internal server error'}{' '}
                    {page === 'payment' && (
                        <Link
                            to="/cart"
                            className="link mb-2"
                            style={{ color: theme.palette.error.dark }}
                        >
                            Back to Cart
                        </Link>
                    )}
                </Alert>
            )}
            <Divider className="mb-3" style={{ backgroundColor: '#4a4a4a' }} />
            {groupedFlatTickets.map((item, index) => (
                <Grid container key={index} className="mb-3">
                    <CartItemSummary flatTickets={item} showDebug={showDebug} />
                </Grid>
            ))}
            {flatTickets !== null && (
                <>
                    <Divider className="mb-2" />
                    <Grid container alignItems="center" className="mb-3">
                        <Grid item xs={6} className="mb-2">
                            <h3>Subtotal</h3>
                        </Grid>
                        <Grid item xs={6}>
                            <h3 className="text-right">{formatMoney(subTotal)}</h3>
                        </Grid>
                        <React.Fragment>
                            <Grid item xs={6} className="mb-2">
                                <h5>Taxes and Fees</h5>
                            </Grid>
                            <Grid item xs={6} className="mb-2">
                                <h5 className="text-right">{formatMoney(taxesAndFees)}</h5>
                            </Grid>
                            <Grid item xs={12} className="mb-2">
                                <Divider />
                            </Grid>
                            <Grid item xs={6} className="mb-2">
                                <h3>Total Charge</h3>
                            </Grid>
                            <Grid item xs={6} className="mb-2">
                                <h3 className="text-right">{formatMoney(totalCharge)}</h3>
                            </Grid>
                            {showCommission ? (
                                <>
                                    <Grid item xs={6} className="mb-2">
                                        <h5>Agent Commission</h5>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <h5 className="text-right">
                                            {formatMoney(sumAgentCommission)}
                                        </h5>
                                    </Grid>
                                </>
                            ) : (
                                ''
                            )}
                        </React.Fragment>
                    </Grid>
                    {showDebug && (
                        <Grid item>
                            <Button size="small" onClick={() => setShowDetails(x => !x)}>
                                Show profit
                            </Button>
                            {showDetails && <pre style={{ fontSize: '80%' }}>{profitDebug}</pre>}
                        </Grid>
                    )}
                    {shouldShowAgentCommissionVarning && (
                        <Grid item xs={12} className="mb-2">
                            <Alert severity="warning">
                                {`This will cause a negative (-) commission to the agency.`}
                            </Alert>
                        </Grid>
                    )}
                    {isAgent && showCartLevelDiscounts && (
                        <Grid
                            container
                            item
                            xs={12}
                            style={{
                                padding: '30px',
                                marginTop: '25px',
                                backgroundColor: '#fafafa',
                                position: 'relative',
                                transition: 'opacity 0.3s linear 0.3s',
                                justifyContent: 'center'
                            }}
                        >
                            <Grid item>
                                <h2>Whole cart discount</h2>
                            </Grid>
                            <DiscountEditor
                                cartItem={{
                                    discount: discount
                                }}
                                // TODO: later it will be sent by backend
                                // we now store the 'ticket_retail_price' in redux, but when we add ticket from ReviewPage the 'retail_price' is equal to the correct 'ticket_retail_price'
                                // so if we get ticket_retail_price we use it, but if we don't, we prefer retail_price
                                maxValue={subTotal}
                                updateCartItem={discount => {
                                    updateCartDiscount(discount);
                                }}
                                showAgencyDiscount={window.clientData.custom_agency_discount}
                                showVendorDiscount={false}
                                discountAmountError={discountAmountError}
                                setDiscountAmountError={setDiscountAmountError}
                                includeWholeCart={true}
                                includePerItem={false}
                            />
                        </Grid>
                    )}
                    <Grid
                        container
                        style={
                            !loading
                                ? {}
                                : {
                                      filter: 'grayscale(1) blur(2px)',
                                      opacity: 0.1,
                                      transition:
                                          'opacity 0.1s linear 0.2s, filter 0.1s linear 0.2',
                                      pointerEvents: 'none',
                                      userSelect: 'none'
                                  }
                        }
                    >
                        {children}
                        {page === 'payment' && (
                            <Grid item xs={12}>
                                <Button
                                    fullWidth
                                    className="btn-squared"
                                    style={{ marginTop: '10px' }}
                                    color="primary"
                                    variant="outlined"
                                    onClick={() =>
                                        showModal(MODAL_TYPE.SHOW_TEST_ITINERARY, {
                                            bookingsData,
                                            customer,
                                            customerArrival,
                                            customerDeparture
                                        })
                                    }
                                >
                                    Check your Itinerary
                                </Button>
                            </Grid>
                        )}
                    </Grid>
                </>
            )}
        </Grid>
    );
};

export default withTheme(BookingSummary);
